import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import {
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  BarController,
  BarElement,
  Tooltip,
} from 'chart.js';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { OperationsAnalytics } from '../../core/models/operations.models';
import { MetricCardComponent } from '../../shared/components/metric-card/metric-card.component';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
);

@Component({
  selector: 'app-operations-page',
  standalone: true,
  imports: [DecimalPipe, FormsModule, BaseChartDirective, MetricCardComponent, LocalDatePipe],
  templateUrl: './operations-page.component.html',
  styleUrl: './operations-page.component.scss',
})
export class OperationsPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<OperationsAnalytics | null>(null);
  readonly interval = signal<'day' | 'week'>('week');

  readonly runVolumeChartData = signal({
    labels: [] as string[],
    datasets: [
      { label: 'Review Runs', data: [] as number[], backgroundColor: '#1d4ed8', borderRadius: 8, borderSkipped: false as const },
      { label: 'Failed', data: [] as number[], backgroundColor: '#dc2626', borderRadius: 8, borderSkipped: false as const },
    ],
  });

  readonly avgDurationChartData = signal({
    labels: [] as string[],
    datasets: [
      { label: 'Avg Duration (s)', data: [] as number[], borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.08)', tension: 0.3, fill: true },
    ],
  });

  readonly barChartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  readonly lineChartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const } },
    scales: { y: { beginAtZero: true } },
  };

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.data.set(null);

    this.analyticsService
      .getOperations({ ...this.dateRange.toQueryParams(), interval: this.interval() })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) this.loading.set(false);
        }),
      )
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) return;
          this.applyOperations(response);
        },
        error: (err: unknown) => {
          if (loadId !== this.currentLoadId) return;
          console.error('Operations request failed', err);
          this.error.set(this.formatError(err, 'Operations data could not be loaded.'));
        },
      });
  }

  changeInterval(value: 'day' | 'week'): void {
    this.interval.set(value);
    this.load();
  }

  formatDurationMs(ms: number): string {
    if (!ms) return '—';
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = seconds / 60;
    return `${minutes.toFixed(1)}m`;
  }

  private applyOperations(ops: OperationsAnalytics): void {
    this.data.set(ops);
    const points = ops.trends?.points ?? [];
    const labels = points.map((p) => p.periodStart.slice(0, 10));

    this.runVolumeChartData.set({
      labels,
      datasets: [
        { ...this.runVolumeChartData().datasets[0], data: points.map((p) => p.reviewRunCount) },
        { ...this.runVolumeChartData().datasets[1], data: points.map((p) => p.failedReviewCount) },
      ],
    });

    this.avgDurationChartData.set({
      labels,
      datasets: [
        { ...this.avgDurationChartData().datasets[0], data: points.map((p) => +(p.avgReviewDurationMs / 1000).toFixed(2)) },
      ],
    });
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') return 'Request timed out after 8s.';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return `Cannot reach backend${error.url ? ` @ ${error.url}` : ''}.`;
      return `${fallback} (${error.status}: ${error.message})`;
    }
    return fallback;
  }
}
